import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';

import { SYSTEMS, normalizeDateSystem } from '../../tools/calendars';
import { CURRENCY_CODES, normalizeCurrency, currencyLabel } from '../../tools/currencies';

// The two settings every MIS document carries and every branch has a default for:
//   · the CALENDAR its dates are entered and printed in (Gregorian, Shamsi/Jalali, Hijri)
//   · the CURRENCY its money is written in
// See api/utils/misDefaults.js for where a new document gets them from.

/** { dateSystem, currency } a branch starts its new documents with (dirhams and Gregorian when unset). */
export const branchDocDefaults = (branch) => ({
  dateSystem: normalizeDateSystem(branch && branch.defaultDateSystem),
  currency: normalizeCurrency(branch && branch.defaultCurrency),
});

const DATE_SYSTEM_KEYS = { gregorian: 'mis.calGregorian', jalali: 'mis.calJalali', hijri: 'mis.calHijri' };

// Native selects, on purpose: these sit inside Drawers that are sometimes raised above modal
// level (so a MUI menu would open behind them - the portal z-index trap), a native list has no
// portal at all, and the arrow keys walk past a closed native select without changing it.
// InputLabelProps.shrink is what keeps the label off a native select's own text.
const fieldProps = (sx) => ({
  select: true, size: 'small', fullWidth: true,
  SelectProps: { native: true },
  InputLabelProps: { shrink: true, style: { fontSize: '0.75rem' } },
  inputProps: { style: { fontSize: '0.8rem' } },
  sx,
});

export function DateSystemSelect({ value, onChange, disabled, sx, label }) {
  const { t } = useTranslation();
  return (
    <TextField {...fieldProps(sx)} label={label || t('mis.fieldDateSystem')} disabled={disabled}
      value={normalizeDateSystem(value)} onChange={(e) => onChange(e.target.value)}>
      {SYSTEMS.map((s) => <option key={s} value={s}>{t(DATE_SYSTEM_KEYS[s])}</option>)}
    </TextField>
  );
}

export function CurrencySelect({ value, onChange, disabled, sx, label, helperText }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  return (
    <TextField {...fieldProps(sx)} label={label || t('mis.fieldCurrency')} disabled={disabled} helperText={helperText}
      value={normalizeCurrency(value)} onChange={(e) => onChange(e.target.value)}>
      {CURRENCY_CODES.map((c) => <option key={c} value={c}>{currencyLabel(c, lang)}</option>)}
    </TextField>
  );
}

export { DATE_SYSTEM_KEYS };
